import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { FOCUS, quitterPleinEcran } from "./outils";

/*
  Signature au doigt, plein écran, en PAYSAGE.

  - Android : l'écran appelant a demandé requestFullscreen() + screen.orientation.lock("landscape")
    (outils.entrerPleinEcranPaysage, dans le clic). La fenêtre est donc déjà en paysage.
  - iPhone (verrouillage non supporté) ou refus : la fenêtre reste en portrait. On fait alors
    pivoter le CONTENEUR de 90° dans le sens horaire :

        largeur = hauteur de la fenêtre (H), hauteur = largeur de la fenêtre (L)
        transform-origin: 0 0 ; transform: translateX(L px) rotate(90deg)

    Un point local (x, y) du conteneur s'affiche à l'écran en (L - y, x) :
      rotate(90deg) envoie (x, y) -> (-y, x), puis translateX(L) -> (L - y, x).
    Le client tourne le téléphone d'un quart de tour vers la gauche pour signer.

  Conversion des coordonnées tactiles -> repère du canvas (point délicat) :
    getBoundingClientRect() du canvas renvoie sa boîte À L'ÉCRAN (après rotation). Dans ce
    repère pivoté, l'axe x du canvas pointe vers le BAS de l'écran et son axe y vers la
    GAUCHE ; son coin haut-gauche local se trouve à l'écran en (rect.right, rect.top). D'où :
        x_canvas = clientY - rect.top
        y_canvas = rect.right - clientX
    Vérification : (rect.right, rect.top) -> (0, 0) ; (rect.left, rect.bottom) ->
    (rect.bottom - rect.top, rect.right - rect.left) = (largeur locale, hauteur locale) du canvas,
    puisque la boîte écran d'un canvas tourné a ses côtés intervertis.
    Sans rotation : x = clientX - rect.left, y = clientY - rect.top.
    La taille du canvas est lue avec clientWidth/clientHeight (taille de mise en page, NON
    affectée par la rotation), jamais avec getBoundingClientRect.

  Le PNG exporté (toDataURL) est donc toujours une image paysage, dans le même format
  qu'avant (data:image/png;base64,...), quel que soit le téléphone.
*/

const EPAISSEUR = 3.5;
const COULEUR = "#0F172A";

const dimensionsFenetre = () => ({ l: window.innerWidth, h: window.innerHeight });

function preparerContexte(ctx, echelle) {
  ctx.setTransform(echelle, 0, 0, echelle, 0, 0);
  ctx.lineWidth = EPAISSEUR;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = COULEUR;
}

// Trace un trait (liste de points) ; un point isolé donne un petit rond (extrémité arrondie)
function tracer(ctx, trait, depuis = 0) {
  if (trait.length <= depuis) return;
  ctx.beginPath();
  ctx.moveTo(trait[depuis].x, trait[depuis].y);
  if (trait.length === 1) ctx.lineTo(trait[0].x + 0.01, trait[0].y);
  for (let i = depuis + 1; i < trait.length; i += 1) ctx.lineTo(trait[i].x, trait[i].y);
  ctx.stroke();
}

export default function SignaturePleinEcran({ nom, onValider, onAnnuler }) {
  const canvasRef = useRef(null);
  const traits = useRef([]); // [[{x, y}, ...], ...] en px CSS, repère local du canvas
  const pointeur = useRef(null); // pointerId du doigt qui dessine
  const [vide, setVide] = useState(true);
  const [fenetre, setFenetre] = useState(dimensionsFenetre);
  const pivote = fenetre.h > fenetre.l; // portrait -> on fait pivoter le contenu
  const pivoteRef = useRef(pivote);
  pivoteRef.current = pivote;

  // Suivi des dimensions de la fenêtre (le verrouillage paysage Android arrive après coup)
  useEffect(() => {
    const maj = () => setFenetre(dimensionsFenetre());
    window.addEventListener("resize", maj);
    window.addEventListener("orientationchange", maj);
    const t1 = window.setTimeout(maj, 350);
    const t2 = window.setTimeout(maj, 1000);
    return () => {
      window.removeEventListener("resize", maj);
      window.removeEventListener("orientationchange", maj);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  // Fermeture (Annuler, Valider ou bouton retour) : on quitte le plein écran et on déverrouille
  useEffect(() => () => quitterPleinEcran(), []);

  // Taille du canvas = sa taille de mise en page x devicePixelRatio ; les traits sont redessinés
  // (un changement de taille efface un canvas : on ne perd pas la signature en cours).
  const redimensionner = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const largeur = c.clientWidth;
    const hauteur = c.clientHeight;
    if (!largeur || !hauteur) return;
    const echelle = window.devicePixelRatio || 1;
    const lp = Math.round(largeur * echelle);
    const hp = Math.round(hauteur * echelle);
    if (c.width !== lp || c.height !== hp) {
      c.width = lp;
      c.height = hp;
    }
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    preparerContexte(ctx, echelle);
    traits.current.forEach((t) => tracer(ctx, t));
  }, []);

  useLayoutEffect(() => {
    redimensionner();
  }, [fenetre, redimensionner]);

  useEffect(() => {
    if (typeof window.ResizeObserver !== "function" || !canvasRef.current) return undefined;
    const ro = new window.ResizeObserver(() => redimensionner());
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [redimensionner]);

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    if (pivoteRef.current) return { x: e.clientY - r.top, y: r.right - e.clientX };
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const debut = (e) => {
    if (pointeur.current !== null) return; // un seul doigt à la fois
    e.preventDefault();
    pointeur.current = e.pointerId;
    try {
      canvasRef.current.setPointerCapture(e.pointerId);
    } catch {
      /* capture indisponible */
    }
    const trait = [point(e)];
    traits.current.push(trait);
    const ctx = canvasRef.current.getContext("2d");
    tracer(ctx, trait);
  };

  const trace = (e) => {
    if (pointeur.current !== e.pointerId) return;
    e.preventDefault();
    const trait = traits.current[traits.current.length - 1];
    const evts = typeof e.nativeEvent.getCoalescedEvents === "function" ? e.nativeEvent.getCoalescedEvents() : [];
    const depart = trait.length - 1; // on repart du dernier point déjà dessiné
    (evts.length ? evts : [e]).forEach((ev) => trait.push(point(ev)));
    tracer(canvasRef.current.getContext("2d"), trait, depart);
    if (vide) setVide(false);
  };

  const fin = (e) => {
    if (pointeur.current !== e.pointerId) return;
    pointeur.current = null;
  };

  const effacer = () => {
    traits.current = [];
    redimensionner();
    setVide(true);
  };

  const valider = () => {
    if (vide || !canvasRef.current) return;
    onValider(canvasRef.current.toDataURL("image/png"));
  };

  const styleConteneur = pivote
    ? {
        position: "absolute",
        top: 0,
        left: 0,
        width: `${fenetre.h}px`,
        height: `${fenetre.l}px`,
        transformOrigin: "0 0",
        transform: `translateX(${fenetre.l}px) rotate(90deg)`,
      }
    : { position: "absolute", inset: 0 };

  return (
    <div
      className="fixed inset-0 z-[100] bg-white overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Signature du client"
      data-testid="signature-plein-ecran"
    >
      <div style={styleConteneur} className="flex flex-col bg-white text-[#0F172A]">
        <div className="flex-none flex items-center gap-3 px-4 py-2 border-b border-[#E2E8F0]">
          <button
            type="button"
            onClick={onAnnuler}
            aria-label="Annuler la signature"
            data-testid="btn-annuler-signature"
            className={`flex-none w-11 h-11 flex items-center justify-center text-[#334155] rounded-lg ${FOCUS}`}
          >
            <X aria-hidden="true" className="w-5 h-5" strokeWidth={2.2} />
          </button>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-[15px] font-bold truncate">Signature du client{nom ? ` — ${nom}` : ""}</span>
            <span className="text-xs text-[#475569] truncate">Lu et approuvé · signez au doigt dans le cadre</span>
          </div>
          <button
            type="button"
            onClick={effacer}
            data-testid="btn-effacer-signature"
            className={`flex-none h-11 px-4 border border-[#CBD5E1] rounded-[10px] bg-white text-[#334155] text-sm font-semibold ${FOCUS}`}
          >
            Effacer
          </button>
          <button
            type="button"
            onClick={valider}
            disabled={vide}
            data-testid="btn-confirmer-signature"
            className={`flex-none h-11 px-5 rounded-[10px] bg-[#0F766E] text-white text-[15px] font-bold disabled:opacity-40 ${FOCUS}`}
          >
            Valider la signature
          </button>
        </div>
        <div className="flex-1 min-h-0 flex px-4 pt-3 pb-4">
          <div className="relative flex-1 border-2 border-dashed border-[#94A3B8] rounded-[14px] bg-[#F8FAFC] overflow-hidden">
            <span aria-hidden="true" className="absolute left-8 right-8 bottom-11 border-t border-[#CBD5E1] pointer-events-none" />
            <span aria-hidden="true" className="absolute left-8 bottom-[18px] text-xs text-[#475569] pointer-events-none select-none">
              Signature
            </span>
            {vide ? (
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-[#64748B] text-lg pointer-events-none select-none"
              >
                Signez ici avec le doigt
              </span>
            ) : null}
            <canvas
              ref={canvasRef}
              data-testid="canvas-signature"
              aria-label="Zone de signature"
              onPointerDown={debut}
              onPointerMove={trace}
              onPointerUp={fin}
              onPointerCancel={fin}
              onLostPointerCapture={fin}
              className="absolute inset-0 w-full h-full"
              style={{ touchAction: "none" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
